import { useEffect, useState } from "react";
import { Deployment, RobotDeployment } from "../../models/Deployment";
import { createRobot, ROBOCORE_BLOB_CONTAINER, Robot } from "../../models/Robot";
import { downloadBlob, loadOrgModel, loadRecords, uploadBlob } from "../../api/connection";
import { Button, IconButton, Typography } from "@mui/material";
import { FlexContainer } from "../../components/FlexContainer";
import { RobotConnectionStatus } from "./RobotConnectionStatus";
import { SignalingConnection } from "../../api/signaling/SignalingConnection";
import { RobotStatusInfo } from "./EditDeploymentPage";
import { RuntimeInfo } from "./pm_interfaces";
import { Delete } from "@mui/icons-material";
import { showConfirmDialog } from "../../components/UniversalDialog";
import JSZip from "jszip";
import { Visual } from "../../models/Visual";
import { RobotData } from "../../visuals/RobotVisual";
import { createModel } from "../../models/Model";
import { createOrgBlob } from "../../models/OrgBlob";
import { downloadOrgBlob } from "../../panels/common/UploadBlob";
import { createSiteAnchor } from "../../models/SiteAnchor";

export function RobotDeploymentEdit({
  deployment,
  robotDeployment,
  statusInfo,
  setDeployment,
}: {
  deployment: Deployment;
  robotDeployment: RobotDeployment;
  statusInfo?: RobotStatusInfo;
  setDeployment: (d: Deployment) => void;
}) {
  const [robot, setRobot] = useState<Robot>();
  const [signaling, setSignaling] = useState<SignalingConnection>();
  const [pmStatus, setPmStatus] = useState<RuntimeInfo>();
  const [deploymentStatus, setDeploymentStatus] = useState('');
  useEffect(() => {
    if (!robotDeployment || !deployment) {
      return;
    }
    (async () => {
      const r = await loadOrgModel(createRobot(robotDeployment.robotId));
      setRobot(r);
      if (statusInfo) {
        const sc = new SignalingConnection({
          deviceId: r._id,
          token: r.token,
          group: "process-manager",
          onReplace: async (dgram) => {
            // console.log("Received replace command:", dgram);
            if (dgram.pmStatus) {
              const status = dgram.pmStatus as RuntimeInfo;
              // console.log("Received pmStatus:", status);
              status.lastUpdate = Date.now();
              setPmStatus(status);
              if (status.deployment && deployment._id === status.deployment._id) {
                setDeploymentStatus(`Correct as of ${new Date(status.deployment.created).toLocaleDateString()} ${new Date(status.deployment.created).toLocaleTimeString()}`);
              } else {
                setDeploymentStatus(`Not Deployed`);
              }
            }
          },
        });
        await sc.connect();
        setSignaling(sc);
      } else if (signaling) {
        signaling.close();
        setSignaling(undefined);
      }
    })();
  }, [robotDeployment, deployment, statusInfo]);

  const uploadPack = async () => {
    if (!robot || !deployment || !signaling) {
      return;
    }
    const packageName = `deployment-pack-${deployment._id}.zip`;
    const zip = new JSZip();
    const recordFolder = zip.folder('records')!;
    const blobFolder = zip.folder('blobs')!;
    recordFolder.file(`deployments-${deployment._id}.json`, JSON.stringify(deployment, null, 2));
    recordFolder.file(`robots-${robot._id}.json`, JSON.stringify(robot, null, 2));
    const site = await loadOrgModel(createSiteAnchor(deployment.siteId));
    recordFolder.file(`siteanchors-${site._id}.json`, JSON.stringify(site, null, 2));
    const visuals = await loadRecords<Visual>('visuals', { siteAnchorId: deployment.siteId });
    for (const v of visuals) {
      console.log('Including visual', v._id, v.type);
      recordFolder.file(`visuals-${v._id}.json`, JSON.stringify(v, null, 2));
      if (v.type === 'QuadrupedVisual') {
        const robotVisualData = v as RobotData;
        if (robotVisualData.robotId === robot._id) {
          const model = await loadOrgModel(createModel(robotVisualData.modelId));
          recordFolder.file(`models-${model._id}.json`, JSON.stringify(model, null, 2));
          if (model.source.blobId) {
            const blob =  await loadOrgModel(createOrgBlob(model.source.blobId));
            recordFolder.file(`orgblobs-${blob._id}.json`, JSON.stringify(blob, null, 2));
            const blobData = await downloadOrgBlob(blob._id);
            blobFolder.file(`${blob._id}.bin`, blobData);
          }
        }
      }
    }
    const zipContent = await zip.generateAsync({ type: "blob" });
    const buffer = await zipContent.arrayBuffer();
    await uploadBlob(ROBOCORE_BLOB_CONTAINER, packageName, buffer);
    signaling.sendReplace({
      deployDb: deployment._id,
    });
  }
  if (!robot) {
    return null;
  }
  return (
    <div style={{ marginTop: 16, marginBottom: 16, padding: 16, border: '1px solid #ccc', borderRadius: 8 }}>
      <FlexContainer>
        <Typography variant="h6">{robot.name} ({robot._id})</Typography>
        <div>
          <RobotConnectionStatus robot={robot} statusInfo={statusInfo} />
          <IconButton
            style={{ marginTop: -16 }}
            size="large"
            color="secondary"
            onClick={async () => {
              if (deployment && robotDeployment) {
                const yes = await showConfirmDialog(
                  "Remove Robot",
                  `Are you sure you want to remove robot ${robot.name} from this deployment?`
                );
                if (yes) {
                  const newRobots = deployment.robots.filter(r => r.robotId !== robotDeployment.robotId);
                  setDeployment({ ...deployment, robots: newRobots });
                }
              }
            }}
          >
            <Delete />
          </IconButton>
        </div>
      </FlexContainer>
      <FlexContainer sx={{ paddingTop: 2, gap: 2 }}>
        <Typography variant='h6'>{deploymentStatus}</Typography>
        <Button
          size='large'
          color='success'
          disabled={!signaling}
          variant="outlined" onClick={uploadPack}>
          Upload
        </Button>
      </FlexContainer>
    </div>
  );
}